<script>
    import config from './../../../config.js'
    import { onMount } from 'svelte'
    import {
        toLegendValue,
        toDateInputValue,
        removeHours,
    } from './../../helpers/datetime.js'
    import { user } from './../../stores.js'
    import Spinner from './../Spinner.svelte'
    import Banner from './../Banner.svelte'
    import Plotly from 'plotly.js-dist-min'

    let from = toDateInputValue(removeHours(new Date(), 24))
    let to = toDateInputValue(new Date())

    export let graphs
    export let layout

    let plot
    let isSpinning = false
    let bannerMsg

    let current_user
    const unsubscribe = user.subscribe((value) => {
        current_user = value
    })

    onMount(async () => {
        let headers = new Headers()
        const { sensor } = config
        if (current_user.token) {
            headers.set('authorization', 'Bearer ' + current_user.token)
        }
        headers.set('Content-Type', 'application/json')
        let defaultFromDate = new Date()
        let hourOffset = 24
        defaultFromDate.setHours(defaultFromDate.getHours() - hourOffset)

        const res = await fetch(sensor['url'], {
            method: 'post',
            headers: headers,
            body: JSON.stringify({
                hours: 36,
                types: ['temperature', 'depth', 'volt'],
                devices: ['laitdepoule', 'bucket-1', 'bucket-2'],
            }),
        })
        const data = await res.json()
        let traces = []
        for (let i = 0; i < graphs.length; i++) {
            const d = graphs[i]['id']
                ? data[graphs[i]['key']][graphs[i]['id']]
                : data[graphs[i]['key']]
            const t = buildTrace(d, graphs[i], i)
            if (!t) continue
            traces.push(t)
        }

        Plotly.newPlot(plot, traces, layout, {
            displayModeBar: false,
            responsive: true,
            scrollZoom: false,
        })
    })

    async function update() {
        isSpinning = true

        let headers = new Headers()

        const { timestamps } = config
        if (current_user.token) {
            headers.set('authorization', 'Bearer ' + current_user.token)
        }
        headers.set('Content-Type', 'application/json')
        let defaultFromDate = new Date()
        defaultFromDate.setHours(defaultFromDate.getHours() - 24)

        let dateFrom = Date.parse(from+' 00:00:00');
        let dateTo = Date.parse(to+' 23:59:59');

        if (dateFrom > dateTo) {
            [dateFrom, dateTo] = [dateTo, dateFrom]
        }

        const res = await fetch(
            timestamps['url'],
            {
                method: 'post',
                headers: headers,
                body: JSON.stringify({
                    timestamp_from: dateFrom,
                    timestamp_to: dateTo,
                    types: ['temperature', 'depth', 'volt'],
                    devices: ['laitdepoule', 'bucket-1', 'bucket-2']
                }),
            }
        ).catch(function (err) {
            console.error(err)
            bannerMsg = err
            isSpinning = false
            return
        })

        if (!res) {
            bannerMsg = 'Response is empty'
            isSpinning = false
            return
        }

        if (res.status === 401) {
            isSpinning = false
            window.location.assign('#login')
        }
        if (res.status === 404) {
            isSpinning = false
            window.location.assign('#404')
        }
        if (!res.ok) {
            isSpinning = false
            console.error(res)
            window.location.assign('#500')
        }

        const data = await res.json()

        isSpinning = false

        let traces = []
        for (let i = 0; i < graphs.length; i++) {
            const d = graphs[i]['id']
                ? data[graphs[i]['key']][graphs[i]['id']]
                : data[graphs[i]['key']]
            const t = buildTrace(d, graphs[i], i)
            if (!t) continue
            traces.push(t)
        }

        Plotly.newPlot(plot, traces, layout, {
            displayModeBar: false,
            responsive: true,
            scrollZoom: false,
        })
    }

    function smooth(values, alpha) {
        var weighted = average(values) * alpha;
        var smoothed = [];
        for (var i in values) {
            var curr = values[i];
            var prev = smoothed[i - 1] || values[values.length - 1];
            var next = curr || values[0];
            var improved = Number(average([weighted, prev, curr, next]).toFixed(2));
            smoothed.push(improved);
        }
        return smoothed;
    }

    function average(data) {
        var sum = data.reduce(function(sum, value) {
            return sum + value;
        }, 0);
        var avg = sum / data.length;
        return avg;
    }

    function buildTrace(
        data,
        { key, name, id, color, width, xaxis, yaxis, transforms, type, hovertemplate, transformY, opacity, mode, shape, smoothing },
        i = 0
    ) {
        if (!data || !data.x || !data.y) return
        let x = data.x.map((part) => {
            let d = new Date(part)
            return d
        });

        let y = transformY ? transformY(data.y) : data.y.map((v) => {
            return parseFloat(v, 10);
        });

        if (smoothing) {
            y = smooth(y, smoothing);
        }

        // accomodate smaller resolution
        if (window.screen.width < 600) {
            x = x.filter(function (v, i) {
                return !(i % 10)
            })
            y = y.filter(function (v, i) {
                return !(i % 10)
            })
        }

        let trace = {
            x: x,
            y: y,
            name: name,
            type: type ? type : 'scatter',
            opacity: opacity ? opacity : 1,
            mode: mode ? mode : 'lines',
            line: {
                color: color,
                width: width,
                shape: shape
            },
            transforms: transforms,
        }

        if (yaxis) {
            trace['yaxis'] = yaxis;
        }
        if (xaxis) {
            trace['xaxis'] = xaxis;
        }
        if (hovertemplate) {
            trace['hovertemplate'] = hovertemplate;
        }

        return trace
    }
</script>

<Spinner hidden={!isSpinning} />

<Banner visible={bannerMsg} type="error">
    <span slot="message">
        {bannerMsg}
    </span>
</Banner>

<form on:submit|preventDefault={update}>
    <input bind:value={from} name="from" type="date" />
    <input bind:value={to} name="to" type="date" />
    <input type="submit" value="&#x21af;" />
</form>
<div bind:this={plot} class="plot"></div>

<style>
    form {
        text-align: center;
    }
    .plot {
        margin-bottom: 20px;
    }
</style>
